import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';

const ListBoxQueryMultiSelect = ({
  fetchData,
  selectedValues = [],
  onChange,
  placeholder = 'Seleziona...',
  openDirection = 'up',
  positionOptions = 'absolute',
  wrapperClassName = '',
}) => {
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const listBoxRef = useRef(null);

  const loadOptions = async (reset = false) => {
    try {
      const nextPage = reset ? 1 : page;
      const newOptions = await fetchData(search, nextPage);
      setOptions(prev => {
        const combined = reset ? newOptions : [...prev, ...newOptions];
        const map = new Map();
        combined.forEach(item => {
          if (!map.has(item.value)) {
            map.set(item.value, item);
          }
        });
        return Array.from(map.values());
      });
      setPage(nextPage + 1);
      setHasMore(newOptions.length > 0);
    } catch (e) {
      console.error('Errore fetch:', e);
    }
  };

  useEffect(() => {
    setPage(1);
    loadOptions(true);
  }, [search]);

  const handleScroll = (e) => {
    const nearBottom = e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight - 10;
    if (nearBottom && hasMore) {
      loadOptions();
    }
  };

  const toggleSelection = (item) => {
    const exists = selectedValues.find(v => v.value === item.value);
    const newSelection = exists
      ? selectedValues.filter(v => v.value !== item.value)
      : [...selectedValues, item];
    onChange && onChange(newSelection.map(i => i.original));
  };

  const listBoxStyles = {
    bottom: openDirection === 'down' ? '100%' : 'auto',
    top: openDirection === 'up' ? '100%' : 'auto',
  };

  return (
    <div className={`w-full ${wrapperClassName}`}>
      <Listbox value={selectedValues} onChange={(selectedItem) => toggleSelection(selectedItem)}>
        <div className="relative mt-1">
          <Listbox.Button className="relative h-10 w-full cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left shadow-md border border-gray-200 sm:text-sm focus:border-none focus:ring-none focus:outline-none">
            <span className="block truncate text-gray-500">
              {selectedValues.length > 0
                ? selectedValues.map(v => v.name).join(', ')
                : placeholder}
            </span>
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options
              ref={listBoxRef}
              onScroll={handleScroll}
              className={`${positionOptions} mt-1 w-full max-h-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-30`}
              style={{ ...listBoxStyles }}
            >
              <div className="px-3 py-2 me-3 bg-white z-10" style={{ position: 'sticky', top: '-4px' }}>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      'ArrowUp',
                      'ArrowDown',
                      'Enter',
                      'Escape',
                      'Tab',
                    ];

                    if (!allowedKeys.includes(e.key)) {
                      e.stopPropagation();
                    }
                  }}
                  placeholder="Cerca..."
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
                />
              </div>
              {options.length > 0 ? (
                options.map((option, idx) => {
                  const selected = selectedValues.find(v => v.value === option.value);
                  return (
                    <Listbox.Option
                      key={idx}
                      value={option}
                      onClick={() => toggleSelection(option)}
                      className={({ active }) =>
                        `relative cursor-pointer top-2 select-none py-2 pl-10 pr-4 ${
                          active ? 'bg-indigo-100 text-gray-900' : 'text-gray-900'
                        }`
                      }
                    >
                      <>
                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                          {option.name}
                        </span>
                        {selected && (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-800">
                            <CheckIcon className="h-5 w-5" />
                          </span>
                        )}
                      </>
                    </Listbox.Option>
                  );
                })
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500">Nessun risultato</div>
              )}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};

export default ListBoxQueryMultiSelect;
